import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Table,
  ButtonAction,
  Row,
} from 'components';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { exportCpdDiplomasToZip } from '../../../api/services/training';

const COLUMN_COUNT = 6;

const CpdDiplomas = ({ loading, list, loadList, setPage, setPerPage, setSort, setFilters }) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const [portalReady, setPortalReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (document.getElementById('cpd-mgmt-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    setSort(direction ? `${key}.${direction}` : null);
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== ''),
      );
      await exportCpdDiplomasToZip(filteredFilters);
    } catch (error) {
      console.error('Error exporting CPD diplomas:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.userName || ''}
                  onFilter={(val) => handleColumnFilterChange('userName', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.diplomas.userPlaceholder')}
                  label={t('page.ma.cpdManagement.diplomas.user')}
                  sort
                  sortDirection={getSortDirection('userName')}
                  onSort={(dir) => handleColumnSort('userName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.name || ''}
                  onFilter={(val) => handleColumnFilterChange('name', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.diplomas.namePlaceholder')}
                  label={t('page.ma.cpdManagement.diplomas.name')}
                  sort
                  sortDirection={getSortDirection('name')}
                  onSort={(dir) => handleColumnSort('name', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.completionDateFrom || ''}
                  filterValueTo={filters.completionDateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('completionDateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('completionDateTo', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.diplomas.completionDate')}
                  label={t('page.ma.cpdManagement.diplomas.completionDate')}
                  sort
                  sortDirection={getSortDirection('completionDate')}
                  onSort={(dir) => handleColumnSort('completionDate', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.courseName || ''}
                  onFilter={(val) => handleColumnFilterChange('courseName', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.diplomas.coursePlaceholder')}
                  label={t('page.ma.cpdManagement.diplomas.course')}
                  sort
                  sortDirection={getSortDirection('courseName')}
                  onSort={(dir) => handleColumnSort('courseName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.ma.cpdManagement.diplomas.cpdHours')}
                  sort
                  sortDirection={getSortDirection('cpdHours')}
                  onSort={(dir) => handleColumnSort('cpdHours', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                <TableHeaderCell
                  label={t('page.ma.cpdManagement.diplomas.actions')}
                  labelAlign="center"
                  isAction={true}
                />
              </CisTable.StickyActionHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={COLUMN_COUNT}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={COLUMN_COUNT}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.diplomas.user')}</Table.Label>
                      {item.userName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.diplomas.name')}</Table.Label>
                      {item.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.diplomas.completionDate')}:</Table.Label>
                      {item.completionDate && moment(item.completionDate).format('DD/MM/YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.diplomas.course')}</Table.Label>
                      {item.courseName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.diplomas.cpdHours')}</Table.Label>
                      {item.cpdHours}
                    </CisTable.Cell>
                    <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                      <Table.Label>{t('page.ma.cpdManagement.diplomas.actions')}:</Table.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        {item.diplomaUrl ? (
                          <ButtonAction
                            as="a"
                            href={item.diplomaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t('page.ma.cpdManagement.diplomas.downloadDiploma')}
                            icon="download"
                          />
                        ) : null}
                        {item.badgeUrl ? (
                          <ButtonAction
                            as="a"
                            href={item.badgeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t('page.ma.cpdManagement.diplomas.viewBadge')}
                            icon="badge"
                          />
                        ) : null}
                      </Row>
                    </CisTable.StickyActionCell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      {portalReady &&
        ReactDOM.createPortal(
          <Button secondary onClick={handleExport} disabled={isExporting}>
            {isExporting ? t('global.exporting') : t('page.ma.cpdManagement.diplomas.exportDisplayed')}
          </Button>,
          document.getElementById('cpd-mgmt-buttons-portal'),
        )}
    </PageContent>
  );
};

CpdDiplomas.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
};

CpdDiplomas.defaultProps = {
  loading: false,
};

export default CpdDiplomas;
